define('services/liveDataService',[
    'lib/bluebird',
    'lib/lodash4',
    'lib/knockout',

    'services/notificationService'
], function (
    Promise,
    _,
    ko,

    notificationService
) {
    'use strict';

    /**
     * Object that tracks subscriptions and data
     * for topics. Structure is:
     * <code>
     * {
     *   topicId: {
     *     topic: topicId,  //redundant, but makes unsubscribe simple
     *     subscriptions: [
     *       {
     *         container: observableArray,
     *         onAdd: addHandler,
     *         TODO: onRemove removeHandler
     *       }
     *     ],
     *   }
     * }
     * </code>
     *
     */
    var topics = {
    };

    /**
     * Mapping from topic types to class constructors. The key is the
     * segment of a dot-delimited topic that determines the type of
     * data handled by the topic, the value is the constructor that
     * should be used to generate entities for that topic.
     */
    var topicClasses = {
        // e.g. calls: Call
        // See registerTopic for how topic classes get added.
    };

    /**
     * Gets liveData and subscription details for a topic.
     * @param {string} topic the topic to get information for
     * @return {object} details about the topic, or undefined if there is no data on the topic
     */
    var getTopicInfo = function (topic) {
        return topics[topic];
    };

    /**
     * Returns the topic information for the topic that is pushing data to the
     * provided container.
     * @param {observableArray} container the container to find the topic for.
     * @return {array} The collection of topics that the container has been registered to.
     *  An empty array will be returned if the container has not registered to any
     */
    var getTopicInfoForData = function (container) {
        return _.filter(topics, function (topic) {
            return _.find(topic.subscriptions, {container: container});
        });
    };

    /**
     * Get the instance of an entity inside of the provided container.
     *
     * @param  {String} id - The id to look up
     * @param  {[type]} containerArg - The container that should used to look up an
     *  instance of the entity that has the provided ID.
     * @return {object} the first entity tracked by the topic that has the provided id
     */
    var findInContainer = function (id, containerArg) {
        var container = ko.unwrap(containerArg);

        var entity;

        if (_.isArray(container)) {
            entity = _.find(container, {id: id});
        } else if (_.has(container, 'id') && container.id === id) {
            entity = container;
        }

        return entity;
    };

    var entityClassForTopicNoCache = function (topic) {

        // Generate the list of topic keys to search amongst. For a given topic
        // "users.123.foo.bar", the list will end up being:
        //
        //  [
        //      "bar",
        //      "foo.bar",
        //      "123.foo.bar",
        //      "users.123.foo.bar",
        //
        //      "foo",
        //      "123.foo",
        //      "users.123.foo",
        //  ]
        //
        //  The reason for this collection and its order to accommodate long key names
        //  when getting the constructor for a topic's class (e.g. a keyname of
        //  "foo.bar").
        //
        //  You may also notice that the last 3 entries are the same as the ones above it,
        //  but with "bar" removed from the end of the strings. This is done to
        //  accommodate topics that end with ids (e.g. we register to "users.123" to look
        //  for changes in a specific user's status). This list should end up in the
        //  latter half of the list to help protect against potential collisions against
        //  key names.
        var generateTopicsArr = function (topicStr) {
            var keysArr = [];
            var topics = topicStr.split('.').reverse();

            var key = '';

            var createTopicKeys = function (t, index) {
                if (index === 0) {
                    key = t;
                } else {
                    key = t + '.' + key;
                }

                keysArr.push(key);
            };

            _.each(topics, createTopicKeys);
            _.each(_.tail(topics), createTopicKeys);

            return keysArr;
        };

        var type = _.find(generateTopicsArr(topic), function (t) {
            return topicClasses[t];
        });

        return topicClasses[type];
    };

    /**
     * Gets the entity class for a given topic based on the topic
     * name. For example, a topic ending in .calls should return
     * data that maps to the Call class.
     *
     * This function is memoized to help make future lookups for a topic's constructor
     * use a results cache without having to re-run the full function again.
     *
     * @param {string} topic the name of the topic
     * @return {function} the constructor function for the corresponding
     * class, or undefined if no mapping exists.
     */
    var entityClassForTopic = _.memoize(entityClassForTopicNoCache);

    /**
     * Performs a delete action against an entity. For a collection container, this
     * function will remove the entity from the collection. For a single entity, the
     * container's value will be set to null.
     *
     * For each subscripion in the provided topicInfo argument, an "onDelete" function will
     * be executed if one is attached to the subscription. If this function returns false,
     * the action of removing the entity will not be performed.
     *
     * @param  {ObservableModel} entity - The entity to remove.
     * @param  {Object} topicInfo - An object containing a collection of subscription
     *  objects that contain the container in which the entity lives.
     *
     */
    var deleteEntity = function (rawData, topicInfo) {
        _.each(topicInfo.subscriptions, function (subscription) {
            var shouldDeleteEntity = true;
            var entity = findInContainer(rawData.id, subscription.container());

            if (entity) {
                if (_.has(subscription, "onDelete") && _.isFunction(subscription.onDelete)) {
                    shouldDeleteEntity = subscription.onDelete(entity);
                }

                if (shouldDeleteEntity) {
                    if (_.isArray(subscription.container())) {
                        subscription.container.remove(entity);
                    } else {
                        subscription.container(null);
                    }
                }
            }
        });
    };

    /**
     * Performs an update action against an entity. If the entity does not exist, a new instance
     * will be created from the rawData attribute.
     *
     * For a collection container, this function will add the entity to the collection.
     * For a single entity, the container's value will be set to null.
     *
     * For each subscription in the provided topicInfo argument, an "onAdd" function will
     * be executed if one is attached to the subscription. If this function returns false,
     * the action of removing the entity will not be performed.
     *
     * @param  {object} rawData - The raw data from the update notification
     * @param  {object} topicInfo - An object containing a collection of subscription
     *  objects that contain the container in which the entity lives.
     * @param {function} The constructor of the class associated with this topic
     *
     */
    var updateEntity = function (rawData, topicInfo, EntityClass) {
        var emptyContainerSubscriptions = [];

        _.each(topicInfo.subscriptions, function (subscription) {
            var entity = findInContainer(rawData.id, subscription.container());

            if (entity) {
                EntityClass.merge(entity, rawData);
                if (subscription.onUpdate) {
                    subscription.onUpdate(entity, subscription.container);
                }
            } else {
                emptyContainerSubscriptions.push(subscription);
            }
        });

        if (emptyContainerSubscriptions.length > 0) {
            // share the instance of the new entity across subscription containers
            var newEntity = new EntityClass(rawData);

            _.each(emptyContainerSubscriptions, function (subscription) {
                if (_.isArray(subscription.container())) {
                    subscription.container.push(newEntity);
                } else if (!subscription.container() || _.isEmpty(subscription.container())) {
                    // if the container is just a single observable that has no value
                    // yet, then set its value
                    subscription.container(newEntity);
                }

                if (subscription.onAdd) {
                    subscription.onAdd(newEntity, subscription.container);
                }

            });
        }

    };

    /**
     * Converts a collection of topic key strings into the type of argument object that
     * notificationService expects for its batch subscription APIs.
     *
     * @param  {String[]} topics A collection of topic key strings.
     * @return {Object} An object whose keys are the topic key names with the values set
     *  as the callbacks that are registered
     */
    var convertTopicsToBatchNotificationArgument = function (topics) {
        return  _.reduce(topics, function (obj, topic) {
            var topicInfo = getTopicInfo(topic);

            obj[topic] = topicInfo.dataHandler;

            return obj;
        }, {});
    };

    /**
     * Creates & stores topic data for use in future subscriptions.
     * @param {string} topic the topic to create data for
     * @return {object} the created topic tracking data.
     */
    var createTopicInfo = function (topic) {
        var
            EntityClass = entityClassForTopic(topic),
            topicInfo = {
                topic: topic,
                subscriptions: []
            };

        if (_.isUndefined(EntityClass)) {
            throw new Error('No liveDataService model constructor is registered for topic: ' + topic);
        }

        topicInfo.dataHandler = function (rawData, options) {
            var action;

            if (_.has(options, "metadata") && _.has(options.metadata, "action")) {
                action = options.metadata.action;
            }

            if (action && action.toLowerCase() === "delete") {
                deleteEntity(rawData, topicInfo);
            } else {
                updateEntity(rawData, topicInfo, EntityClass);
            }
        };

        topics[topic] = topicInfo;
        return topicInfo;
    };

    /**
     * Subscribes to the notification service for live updates on a topic.
     * @param {string} topic the topic to subscribe to
     * @return {promise} a promise that resolves when subscription has completed
     */
    var notificationSubscribe = function (topic) {
        var topicInfo = getTopicInfo(topic);
        return notificationService.subscribe(topic, topicInfo.dataHandler);
    };

    /**
     * Subscribe a collection of topics to notifications.
     *
     * @param  {String[]} topics An array containing a collection of strings that will be
     *  used as the topics to subscribe to.
     * @return {Promise} a promise that resolves when subscription has completed
     */
    var batchNotificationSubscribe = function (topics) {
        var batchTopics = convertTopicsToBatchNotificationArgument(topics);

        return notificationService.batchSubscribe(batchTopics);
    };

    /**
     * Unsubscribes from live updates from the notification service for a collection of
     * topics.
     * @param {String[]} topics - A collection of topic strings to unsubscribe from.
     * @return {Promise} a promise that resolves when unsubscription has completed
     */
    var batchNotificationUnsubscribe = function (topics) {
        var batchTopics = convertTopicsToBatchNotificationArgument(topics);

        return notificationService.batchUnsubscribe(batchTopics);
    };

    /**
     * Adds a subscriber to a topic.
     * @param {object} topicInfo data about the topic
     * @param {observableArray} container container that will track data for this subscription.
     * @param {function} addHandler optional function to be called when new objects are added to
     * the container.
     * @return {promise} a promise that resolves when subscription has completed
     */
    var addSubscriber = function (topicInfo, container, options) {
        var subscriptionData = _.assign({}, options, {
            container: container
        });

        topicInfo.subscriptions.push(subscriptionData);

        //Start notificaton service if this is the first subscriber
        if (topicInfo.subscriptions.length == 1) {
            return notificationSubscribe(topicInfo.topic);
        } else {
            return Promise.resolve();
        }
    };

    /**
     * Add a subscriber to a collection of topics.
     *
     * @param  {Object[]} topicInfoArr - A collection of objects containing data about a
     *  given topic.
     * @param  {ObservableArray} container - container that will track data for each subscription.
     * @return {Promise} a promise that resolves when subscription has completed
     */
    var batchAddSubscribers = function (topicInfoArr, container, options) {
        var subscriptionData = _.assign({}, options, {
            container: container
        });

        var topicsToSubscribe = _.reduce(topicInfoArr, function (collection, topicInfo) {
            topicInfo.subscriptions.push(subscriptionData);

            if (topicInfo.subscriptions.length === 1) {
                collection.push(topicInfo.topic);
            }

            return collection;
        }, []);

        if (topicsToSubscribe.length > 0) {
            return batchNotificationSubscribe(topicsToSubscribe);
        } else {
            return Promise.resolve();
        }
    };

    /**
     * Removes a subscriber from a collection of topics.
     * @param  {Object[]} topicInfoArr - A collection of objects containing data about a
     *  given topic.
     * @param {ObservableArray} container - container that is tracking data for a subscription.
     * @return {promise} a promise that resolves when unsubscription has completed
     */
    var batchRemoveSubscribers = function (topicInfoArr, container) {
        var topicsToUnsubscribe = _.reduce(topicInfoArr, function (collection, topicInfo) {
            topicInfo.subscriptions = _.reject(topicInfo.subscriptions, {container: container});

            if (topicInfo.subscriptions.length === 0) {
                collection.push(topicInfo.topic);
            }

            return collection;
        }, []);

        if (topicsToUnsubscribe.length > 0) {
            return batchNotificationUnsubscribe(topicsToUnsubscribe);
        } else {
            return Promise.resolve();
        }
    };


    /**
     * Service that can handle requesting a live data represntation of a
     * topic. The live data is represented as an observable array of
     * {@link ObservableObjects}.
     * @exports services/liveDataService
     */
    var liveDataService = {
        /**
         * Observable indicating if the live data connection is currently
         * active.
         */
        connected: notificationService.connected,

        /**
         * Function that requests live data for a specific topic.
         *
         * @param {String|String[]} topic - the topic(s) to get live data for.
         * @param {Observable} container - the container the live data should
         *  be maintained in. If it is a plain observable, a single entity
         *  can be made live. If it is an observable array, a collection of live
         *  entities will be managed.
         * @param {Object} [options=undefined] - An object containing optional arguments
         *  to the function.
         * @param {Function} [options.onAdd=undefined] - callback that will be called
         *  whenever a new entity is added to the container. The arguments passed to the
         *  callback will be the added entity and the container passed to 'getLiveData'
         * @param {Function} [options.onDelete=undefined] - callback that will be called
         *  whenever a new entity will be removed from the container. The arguments passed
         *  to the callback will be the removed entity. This function should return true.
         *  If a falsy value is returned, the entity will NOT be removed from the
         *  container.
         *
         * @return {promise} A promise that resolves once the live data subscription
         *  is fully set up.
         */
        getLiveData: function (topic, container, options) {
            var containerSubscriptionTopics = getTopicInfoForData(container);

            if (_.isArray(topic)) {
                // Generate the collection of topicInfo objects that need to register for
                // notifications. We use _.reduce in order to go through the topics array
                // once to filter out topics that the container has already subscribed to
                // while generating the new subscriptions.
                var topicInfoArr = _.reduce(topic, function (topicInfos, topicKey) {

                    var existingSubscription = _.find(containerSubscriptionTopics, {topic: topicKey});

                    if (_.isUndefined(existingSubscription)) {
                        // if the container has not subscribe to the topic key yet, lets
                        // subscribe to it.
                        var topicInfo = getTopicInfo(topicKey);

                        if (!topicInfo) {
                            topicInfo = createTopicInfo(topicKey);
                        }

                        topicInfos.push(topicInfo);
                    }

                    return topicInfos;
                }, []); // start the collection with an empty array.

                return batchAddSubscribers(topicInfoArr, container, options);
            }

            // Topic is a string, so we are registering the container to a single topic
            var topicInfo = getTopicInfo(topic);

            if (containerSubscriptionTopics.length === 0) {
                if (!topicInfo) {
                    topicInfo = createTopicInfo(topic);
                }
                return addSubscriber(topicInfo, container, options);
            } else {
                return Promise.resolve();
            }
        },

        /**
         * Cancels a request for live data about a specific topic. Canceling does
         * not guarantee that the live data will not be further updated, as other
         * entities may still be using it.
         * @param {object} subscription the subscription to cancel
         * @return {promise} a promise that resolves when the subscription is canceled
         */
        cancelLiveData: function (container) {
            var topicInfoArr = getTopicInfoForData(container);

            return batchRemoveSubscribers(topicInfoArr, container);
        },

        /**
         * Registers a topic with a model constructor.
         * @param topic {String} e.g. 'edges' for 'edges.{id}' notifications
         * @param ModelConstructor {Function} A constructor used to create new objects on an add notification
         * and merge in new changes via ModelConstructor.merge on change notifications.
         */
        registerTopic: function(topic, ModelConstructor) {

            var EntityClass = entityClassForTopic(topic);
            if (_.isUndefined(EntityClass)) {
                topicClasses[topic] = ModelConstructor;
                // Re-memoize the topics based on the new topic registration, otherwise they will be outdated.
                entityClassForTopic = _.memoize(entityClassForTopicNoCache);
            } else if (ModelConstructor !== EntityClass) {
                throw new Error(topic + ' is already registered with a different model constructor.');
            }
        }
    };

    return liveDataService;
});

